import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import type { PlatformProfileUserRole } from '@nucleus-commerce/api-client/platform';
import { ApiError } from '@nucleus-commerce/api-client/transport';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { ServerError } from '@/shared/ui/server-error';

import { toCreateUserRequest } from '../api/mappers';
import { useCreateUser } from '../hooks/use-create-user';
import {
  createUserSchema,
  emptyCreateUserForm,
  type CreateUserInput,
  type CreateUserOutput,
} from '../schemas/create-user-schema';
import { assignableRoles } from '../schemas/user-rules';

import { RoleSelect } from './role-select';

export interface CreateUserDialogProps {
  actorRole: PlatformProfileUserRole;
}

/** Creates the account and emails a 7-day set-password link; there is no password field on purpose. */
export function CreateUserDialog({ actorRole }: CreateUserDialogProps) {
  const { t } = useTranslation(['users', 'common']);
  const [open, setOpen] = useState(false);
  const create = useCreateUser();
  const roles = assignableRoles(actorRole);
  const form = useForm<CreateUserInput, unknown, CreateUserOutput>({
    resolver: useI18nResolver(createUserSchema),
    defaultValues: emptyCreateUserForm(),
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      form.reset(emptyCreateUserForm());
      create.reset();
    }
  };

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await create.mutateAsync(toCreateUserRequest(values));
      onOpenChange(false);
    } catch (error) {
      if (error instanceof ApiError && error.code === 'ALREADY_EXISTS') {
        form.setError('email', { type: error.code, message: t('users:errors.emailTaken') });
        return;
      }
      applyFieldErrors(error);
    }
  });

  const showServerError =
    create.isError && !create.error.hasFieldErrors && !form.formState.errors.email;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-tour="platform.users.create">
          <Plus aria-hidden />
          {t('users:create')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('users:createDialog.title')}</DialogTitle>
          <DialogDescription>{t('users:createDialog.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
            {showServerError ? (
              <ServerError
                error={create.error}
                message={create.error.kind === 'forbidden' ? t('users:errors.ownerOnly') : null}
              />
            ) : null}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('users:fields.name')}</FormLabel>
                  <FormControl>
                    <Input autoComplete="off" autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('users:fields.email')}</FormLabel>
                  <FormControl>
                    <Input type="email" autoComplete="off" {...field} />
                  </FormControl>
                  <FormDescription>{t('users:fields.emailHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('users:fields.role')}</FormLabel>
                  <FormControl>
                    <RoleSelect value={field.value} onChange={field.onChange} roles={roles} />
                  </FormControl>
                  {actorRole === 'owner' ? null : (
                    <FormDescription>{t('users:fields.roleAdminHint')}</FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending
                  ? t('users:createDialog.submitting')
                  : t('users:createDialog.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
